import { Component, ReactNode, ErrorInfo } from "react";
import { SkyButton } from "@skypointcloud/skypoint-ui";
import { AlertCircle, RefreshCw } from "lucide-react";
import { logger } from "../../logger-init";

interface RouteErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
  onReset?: () => void;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const message = error.message?.toLowerCase() || "";
  const name = error.name?.toLowerCase() || "";
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    message.includes("failed to load module script") ||
    message.includes("preload") ||
    name.includes("chunkloaderror") ||
    (error instanceof TypeError && message.includes("failed to fetch"))
  );
}

class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const isChunk = isChunkLoadError(error);
    logger.error("Route error caught by RouteErrorBoundary", error, {
      component: "RouteErrorBoundary",
      componentStack: errorInfo?.componentStack,
      isChunkError: isChunk,
    });

    if (isChunk) {
      const reloadKey = "skysupport_chunk_reload";
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
        return;
      }
      sessionStorage.removeItem(reloadKey);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, isChunkError: false });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleRefresh = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallbackTitle = "Something went wrong" } = this.props;

      return (
        <div className="flex flex-col justify-center items-center h-full min-h-[300px] w-full gap-4 p-8 text-center bg-white dark:bg-gray-900">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {fallbackTitle}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-[400px]">
              An error occurred while loading this section. Please try refreshing the page.
            </p>
          </div>
          <div className="flex gap-3 mt-2">
            <SkyButton
              appearance="primary"
              icon={RefreshCw}
              onClick={this.handleRefresh}
            >
              Refresh Page
            </SkyButton>
            <SkyButton
              appearance="secondary"
              icon={RefreshCw}
              onClick={this.handleRetry}
            >
              Try Again
            </SkyButton>
          </div>
        </div>
      );
    }

    sessionStorage.removeItem("skysupport_chunk_reload");
    return this.props.children;
  }
}

export default RouteErrorBoundary;
